import { CreateAccountScreen } from 'components/create-account';
import { CreateAccountSentScreen } from 'components/create-account-sent-screen';
import { ForgotPasswordScreen } from 'components/forgot-password-screen';
import { ForgotPasswordSentScreen } from 'components/forgot-password-sent-screen';
import { LoggingInScreen } from 'components/logging-in-screen';
import { LoginScreen } from 'components/login-screen';
import { ResetPasswordScreen } from 'components/reset-password-screen';
import { Screen, ScreenContext, ScreenStatic } from 'components/screen';
import { VerifyIdentityScreen } from 'components/verify-identity-screen';
import { AccountIdPGetModel, AccountIdPGetResponse } from 'controllers/account-idp.get.controller';
import { AccountValidateCountryGetModel, AccountValidateCountryResponse } from 'controllers/account-validate-country.get.controller';
import { AccountPasswordPostModel } from 'controllers/account-password.post.controller';
import { AccountPasswordPutModel, AccountPasswordPutResponse } from 'controllers/account-password.put.controller';
import { AccountVerificationPutModel, AccountVerificationPutResponse } from 'controllers/account-verification.put.controller';
import { BaseResponse } from 'controllers/controller';
import { SessionPostResponse } from 'controllers/session.post.controller';
import getQueryValue from 'helpers/get-query-value';
import log from 'helpers/log';
import { SAMLIdPModel } from 'models/saml-idp.model';
import EventEmitter = require('wolfy87-eventemitter');
import find = require('lodash/find');
import dialogHTML = require('html-loader!html/dialog');
import generalCSS = require('html-loader!css/general');
import cookies = require('js-cookie');

enum RequestMethod {
  GET,
  POST,
  PUT,
  DELETE,
}

const SESSION_TOKEN_COOKIE_KEY = 'sessionToken';

/**
 * Public interface for Marin SSO JavaScript SDK.
 */
export class MarinSSO extends EventEmitter {
  public siteID: string;
  private static SCREENS: Array<ScreenStatic>;
  protected endpoint: string;

  constructor(endpoint: string, siteID: string, passwordResetToken?: string) {
    super();
    this.endpoint = endpoint;
    this.siteID = siteID;

    // Add new screens here.
    MarinSSO.SCREENS = [
      LoginScreen,
      ForgotPasswordScreen,
      ForgotPasswordSentScreen,
      ResetPasswordScreen,
      LoggingInScreen,
      VerifyIdentityScreen,
      CreateAccountScreen,
      CreateAccountSentScreen,
    ];

    // Append general CSS.
    const styles = document.createElement('style');
    styles.innerHTML = generalCSS;
    document.head.appendChild(styles);

    // Listen for ESC key and close dialog (if open) when pressed.
    document.onkeydown = (evt) => {
      evt = evt || window.event;
      let isEscape = false;
      if ('key' in evt) {
        isEscape = evt.key === 'Escape' || evt.key === 'Esc';
      } else {
        isEscape = (evt as any).keyCode === 27;
      }

      if (isEscape) {
        const dialog = document.getElementById('msso-dialog');
        if (dialog) {
          dialog.remove();
        }
      }
    };

    this.on('login', (session) => {
      this.setSessionCookie(session.sessionToken);
    });

    this.on('logout', () => {
      this.clearSessionCookie();
    });
  }

  public loginWithSAMLIdP({ idp }: { idp: SAMLIdPModel }): void {
    window.location.href = `${this.endpoint}saml/idp/auth/login/${encodeURIComponent(idp.entityID)}?redirectURL=${encodeURIComponent(
      window.location.toString()
    )}`;
  }
  public loginWithSAMLIdPEntityId({ entityID }: { entityID: string }): void {
    window.location.href = `${this.endpoint}saml/idp/auth/login/${encodeURIComponent(entityID)}?redirectURL=${encodeURIComponent(
      window.location.toString()
    )}`;
  }

  public async login({
    loginID,
    password,
    entityID,
  }: {
    loginID: string;
    password: string;
    entityID?: string;
  }): Promise<SessionPostResponse & BaseResponse> {
    const response = await this.request(RequestMethod.POST, 'session', {
      loginID,
      password,
      entityID,
    });
    this.emit('login', response);
    return response;
  }

  public async logout(): Promise<{ success: boolean }> {
    const response = await this.request(RequestMethod.DELETE, 'session');
    this.emit('logout', response);
    return response;
  }

  public async getSession(): Promise<{ success: boolean; account: Account }> {
    try {
      const result = await this.request(RequestMethod.GET, 'session');
      this.setSessionCookie(result.sessionToken);
      return result;
    } catch (e) {
      this.clearSessionCookie();
      throw e;
    }
  }

  public getAccountIdP({ loginID }: AccountIdPGetModel): Promise<AccountIdPGetResponse & BaseResponse> {
    return this.request(RequestMethod.GET, 'account/idp', { loginID });
  }

  public validateRequestCountry({ ip }: AccountValidateCountryGetModel): Promise<AccountValidateCountryResponse> {
    return this.request(RequestMethod.GET, 'account/validate-country', { ip });
  }

  public forgotPassword({ loginID, newAccountSetup = 'false', siteID = '' }: AccountPasswordPostModel): Promise<{ success: boolean }> {
    return this.request(RequestMethod.POST, 'account/password', {
      loginID,
      newAccountSetup,
    });
  }

  public async createAccount(params: SignUpParams): Promise<any> {
    const marinAPIEndpoint = `${window?.SETTINGS?.api?.endpoint}/signup`;
    let url = window.location.origin + marinAPIEndpoint;

    // Config in local environment is different than server
    if (marinAPIEndpoint.indexOf('localhost') > -1) {
      url = marinAPIEndpoint;
    }

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    }).then((response) => {
      // 1. check response.ok
      if (response.ok) {
        return response?.json();
      }
      return response.text().then((text) => {
        // parse the json text response
        throw new Error(JSON.parse(text));
      });
    });
  }

  public async changePassword({
    oldPassword,
    newPassword,
    passwordResetToken,
  }: {
    oldPassword?: string;
    newPassword: string;
    passwordResetToken?: string;
  }): Promise<AccountPasswordPutResponse> {
    const request: AccountPasswordPutModel = {
      siteID: this.siteID,
      newPassword,
      oldPassword,
      passwordResetToken,
      autoLogin: true, // Only used when not logged in.
    };
    const response: AccountPasswordPutResponse = await this.request(RequestMethod.PUT, 'account/password', request);
    if (response.sessionToken) {
      this.emit('login', response);
    }
    return response;
  }

  public async verifyIdentity({ password }: { password: string }): Promise<AccountVerificationPutResponse> {
    const response = await this.request(RequestMethod.PUT, 'account/verification', {
      siteID: this.siteID,
      password,
    } as AccountVerificationPutModel);
    this.emit('login', response);
    return response;
  }

  public getCreateAccountScreenID(): string {
    return CreateAccountScreen.screenID;
  }

  public renderScreen({ screenID, container }: { screenID: string; container?: HTMLElement }, context: ScreenContext = {}): Screen {
    if (!screenID) {
      throw new Error('Missing parameter: screenID');
    }

    // Check to see if we're redirecting to password reset screen automatically.
    const queryValuePasswordResetToken = getQueryValue('passwordResetToken');
    const signUpParam = getQueryValue('sign_up');
    if (screenID === LoginScreen.screenID && queryValuePasswordResetToken) {
      // The password reset token must be cached at this point because it may be automatically cleared.
      context.source = 'passwordResetTokenQueryString';
      context.passwordResetToken = queryValuePasswordResetToken;

      // Redirect to reset-password screen.
      screenID = ResetPasswordScreen.screenID;
    }

    // Switch to create screen automatically only if there is a sign up param and settings api endpoint is defined and its not already rendered
    if (screenID === LoginScreen.screenID && signUpParam && window?.SETTINGS?.api?.endpoint && !CreateAccountScreen.context) {
      screenID = CreateAccountScreen.screenID;
    }

    // Identify screen to load via screen ID on static interface.
    let screenConstructor = find(MarinSSO.SCREENS, (screen) => screen.screenID === screenID);
    if (!screenConstructor) {
      throw new Error(`Undefined screenID: ${screenID}`);
    }

    // If no container is provided, render in dialog.
    let dialog;
    if (!container) {
      // Check for existing dialog.
      dialog = document.getElementById('msso-dialog');

      // Create dialog if necessary.
      if (!dialog) {
        dialog = document.createElement('div');
        dialog.id = 'msso-dialog';
        dialog.innerHTML = dialogHTML;
        document.body.appendChild(dialog);
      }

      // Find inner-containr.
      container = dialog.querySelector('#msso-dialog-content') as HTMLElement;
    }

    // Render screen.
    const screen = new screenConstructor(container, context, dialog);
    screen.render();

    this.emit('screenChange', screenID);

    return screen;
  }

  protected request(requestMethod: RequestMethod, path: string, data: { [key: string]: any } = {}, endpoint?: string): Promise<any> {
    // Convert method to string.
    const method = RequestMethod[requestMethod];

    // Always send site ID and trigger CORS.
    data['siteID'] = this.siteID;
    data['xhr'] = true;

    return new Promise((resolve, reject) => {
      let url = `${endpoint || this.endpoint}${path}`;
      const xhr = new XMLHttpRequest();
      let formData: FormData | undefined = undefined;

      // Enable cookies to be transferred across domains.
      xhr.withCredentials = true;

      // Handle form data.
      if (requestMethod === RequestMethod.POST) {
        // Convert to FormData for POST.
        formData = new FormData();
        for (const key in data) {
          if (data[key] !== undefined && formData) {
            formData.append(key, data[key]);
          }
        }
      } else {
        for (const key in data) {
          if (data[key] !== undefined) {
            const value = data[key];
            url += `&${key}=${encodeURIComponent(value)}`;
          }
        }

        // Replace first & with ?.
        url = url.replace('&', '?');
      }

      // Open request.
      xhr.open(method, url, true);

      // Bind event handlers.
      xhr.onload = (ev) => {
        const response = JSON.parse(xhr.responseText);
        this.emit('response', { method, path, data, response });
        if (response.success) {
          resolve(response);
        } else {
          reject(response);
        }
      };
      xhr.onerror = (error) => {
        const response = {
          success: false,
          errorMessage: 'REQUEST_FAILED',
          statusCode: 400,
          errorDetails: error,
        };
        log.error('XHR error', error);
        this.emit('response', { method, path, data, error, response });
        reject(response);
      };

      // Send request.
      xhr.send(formData);
    });
  }

  /**
   * Set session token on cookie
   */
  private setSessionCookie(sessionToken) {
    const secure = window.location.protocol === 'https:';
    cookies.set(SESSION_TOKEN_COOKIE_KEY, sessionToken, {
      SameSite: 'none',
      secure,
    });
  }

  /**
   * Clear session cookie
   */
  private clearSessionCookie() {
    const secure = window.location.protocol === 'https:';
    cookies.remove(SESSION_TOKEN_COOKIE_KEY, { SameSite: 'None', secure });
  }
}

interface SignUpParams {
  email: string;
  companyName: string;
  country: string;
  firstName: string;
  state: string;
  postalCode: string;
  lastName: string;
  monthlySpend: string;
}

export default MarinSSO;
